import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import PurchaseInvoicePrintView from './PurchaseInvoicePrintView';

const PublicInvoiceViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showZoomWarning, setShowZoomWarning] = useState(false);
  const [showPrintOverlay, setShowPrintOverlay] = useState(false);
  const printOverlayRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    setShowPrintOverlay(true);
    setTimeout(() => {
      window.print();
      setShowPrintOverlay(false);
    }, 150); // Allow time for overlay render
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await apiService.get(`/invoices/${id}/public`);
        setInvoice(res.data);
      } catch (err: any) {
        console.error('Error fetching invoice:', err);
        if (err.response?.status === 404) {
          setError('Invoice not found.');
        } else if (err.response?.status === 500) {
          setError('Server error. Please try again later.');
        } else {
          setError('Failed to load invoice details. Please check the URL and try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id]);

  // Detect browser zoom
  useEffect(() => {
    const checkZoom = () => {
      setShowZoomWarning(window.devicePixelRatio !== 1);
    };
    checkZoom();
    window.addEventListener('resize', checkZoom);
    return () => window.removeEventListener('resize', checkZoom);
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div>;
  }
  if (error) {
    return <div className="text-center text-red-600 p-8">{error}</div>;
  }
  if (!invoice) {
    return <div className="text-center text-gray-500 p-8">Invoice not found.</div>;
  }

  // Render the print view as a pixel-perfect replica
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#232c3d] relative">
      {/* Print Button */}
      <button
        onClick={handlePrint}
        className="absolute top-8 right-8 z-50 bg-blue-600 text-white px-6 py-2 rounded shadow hover:bg-blue-700 print:hidden font-semibold text-base"
      >
        Print
      </button>
      {showZoomWarning && (
        <div
          className="fixed top-0 left-0 w-full text-center z-[1000] bg-yellow-300 py-4 font-bold text-[var(--color-text)]"
        >
          <span className="mr-4">Browser zoom is not 100%. For a true-to-size preview and perfect PDF, please reset your browser zoom (Cmd+0 or Ctrl+0).</span>
          <button
            onClick={() => setShowZoomWarning(false)}
            className="bg-white text-[var(--color-text)] border border-gray-400 rounded px-3 py-1 cursor-pointer font-medium"
          >
            Dismiss
          </button>
        </div>
      )}
      <div className="bg-white w-[794px] h-[1123px] p-8 flex items-center justify-center rounded-none shadow-none">
        <PurchaseInvoicePrintView
          invoice={invoice}
          settings={{}}
          currentPage={1}
          itemsPerPage={100}
          printMode={true}
        />
      </div>
      {/* Print Overlay - visible only when printing */}
      {showPrintOverlay && (
        <div className="fixed top-0 left-0 w-screen h-screen bg-white z-[9999] flex items-center justify-center">
          <div className="w-[794px] h-[1123px] bg-white flex items-center justify-center">
            <PurchaseInvoicePrintView
              invoice={invoice}
              settings={{}}
              currentPage={1}
              itemsPerPage={100}
              printMode={true}
            />
          </div>
        </div>
      )}
      <style>{`
        @media print {
          body > *:not(.print-only) { display: none !important; }
          .print-only { display: flex !important; }
        }
      `}</style>
    </div>
  );
};

export default PublicInvoiceViewPage; 